import {getTs, getStartOfDay} from '../../../../../library/dates';
import {tsToDate} from '../../../../../library/dates';
import getSpecialTranslation from '../../../../../functions/getSpecialTranslation';
import {SelectedEvent, EventStatus, EventKind, EventData} from './types';
import {TEvent} from '../../../../../redux/event/event.type';
import {DEFAULT_TIME_ZONE} from '../../../../../globalVariables/locals';

// Constants for hard-coded strings
export const EVENT_STATUSES = {
    ALL: 'all',
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    EXPIRED: 'expired',
    DRAFT: 'draft'
} as const;

export const EVENT_KINDS = {
    MANDATORY: 'mandatory',
    OPTIONAL: 'optional'
} as const;

// Additional types for utils
export interface EventCategory {
    catId: string;
    title: string;
}

export interface FilterOption {
    key: string;
    title: string;
    onClick: () => void;
}
export const DRAFT = 4;
const isDraftEvent = (event: TEvent): boolean => event.ready === DRAFT;

// Event status utilities
export const getEventStatus = (event: TEvent): EventStatus => {
    const now = getTs();
    if(event.timeEnd && event.timeEnd < now) return EVENT_STATUSES.EXPIRED;
    if(isDraftEvent(event)) return EVENT_STATUSES.DRAFT;
    return event.active ? EVENT_STATUSES.ACTIVE : EVENT_STATUSES.INACTIVE;
};

export const isEventOutsideSaleValidity = (event: TEvent, saleStart?: number, saleEnd?: number): boolean => {
    if(!saleStart && !saleEnd) return false;
    const eventTime = event.timeStart;
    if(!eventTime) return false;
    const timeZone = event.timeZone || DEFAULT_TIME_ZONE;
    const eventDay = getStartOfDay(eventTime, timeZone);
    const isWithinRange =
        (!saleStart || eventDay >= getStartOfDay(saleStart, timeZone))
        && (!saleEnd || eventDay <= getStartOfDay(saleEnd, timeZone));
    return !isWithinRange;
};

export const getAssignableActiveEvents = (events: TEvent[], saleStart?: number, saleEnd?: number): TEvent[] =>
    events.filter(event => !isEventOutsideSaleValidity(event, saleStart, saleEnd));

export const getStatusText = (status: string, t: (key: string) => string): string => {
    const statusTexts: {[key: string]: string} = {
        [EVENT_STATUSES.ALL]: t('allStatuses'),
        [EVENT_STATUSES.ACTIVE]: t('active'),
        [EVENT_STATUSES.INACTIVE]: t('inactive'),
        [EVENT_STATUSES.EXPIRED]: t('expired'),
        [EVENT_STATUSES.DRAFT]: t('draft')
    };
    return statusTexts[status];
};

// Category utilities
export const getCategoryText = (category: string, tLayout: (key: string) => string): string => {
    return getSpecialTranslation(`category${category}`, tLayout) || tLayout('category0');
};

// Category text for filter dropdown menus
export const getCategoryFilterText = (categoryId: string, eventCategories: EventCategory[], t: (key: string) => string): string => {
    if(categoryId === EVENT_STATUSES.ALL) return t('allCategories');
    const category = eventCategories?.find((cat: EventCategory) => cat.catId === categoryId);
    return category?.title || t('allCategories');
};

// Event kind utilities
export const getEventKindText = (eventKind: string | null, t: (key: string) => string): string => {
    if(!eventKind) return t('eventStatus');
    return eventKind === EVENT_KINDS.MANDATORY ? t('mandatory') : t('optional');
};

// Event selection utilities - using Set for O(1) performance
export const isEventSelected = (eid: number, selectedEventIds: Set<number>): boolean => {
    return selectedEventIds.has(eid);
};

// Helper to create Set from SelectedEvent array
export const createSelectedEventIdsSet = (selectedEvents: SelectedEvent[]): Set<number> => {
    return new Set(selectedEvents.map(se => se.eid));
};

// Helper to create Map from SelectedEvent array for quick lookup of isMandatory
export const createSelectedEventsMap = (selectedEvents: SelectedEvent[]): Map<number, boolean> => {
    return new Map(selectedEvents.map(se => [se.eid, se.isMandatory]));
};

export const getEventKind = (eid: number, selectedEventsMap: Map<number, boolean>): EventKind | null => {
    const isMandatory = selectedEventsMap.get(eid);
    if(isMandatory === undefined) return null;
    return isMandatory ? EVENT_KINDS.MANDATORY : EVENT_KINDS.OPTIONAL;
};

// Event data transformation
export const transformEventToTableData = (event: TEvent, selectedEventIds: Set<number>, selectedEventsMap: Map<number, boolean>): EventData => {
    const isSelected = isEventSelected(event.eid, selectedEventIds);
    const status = getEventStatus(event);
    const eventKind = getEventKind(event.eid, selectedEventsMap);
    
    return {
        id: event.eid,
        selected: isSelected,
        title: event.title || '-',
        date: tsToDate(event.timeStart, '', event.timeZone),
        time: tsToDate(event.timeStart, '', event.timeZone, 'HH:mm'),
        category: event.category || '-',
        location: event.location || '-',
        status: status,
        eventKind: eventKind,
        event: event
    };
};

// Event kind options for dropdown menus
export const getEventKindOptions = (eventId: number, handleEvent: (type: 'change', data: {id: number, name: 'eventKind', value: string}) => void, t: (key: string) => string, onClose: () => void): FilterOption[] => [
    {
        key: EVENT_KINDS.OPTIONAL,
        title: t('optional'),
        onClick: () => {
            handleEvent('change', {id: eventId, name: 'eventKind', value: EVENT_KINDS.OPTIONAL});
            onClose();
        }
    },
    {
        key: EVENT_KINDS.MANDATORY,   
        title: t('mandatory'),
        onClick: () => {
            handleEvent('change', {id: eventId, name: 'eventKind', value: EVENT_KINDS.MANDATORY});
            onClose();
        }
    }
];

// Status filter options
export const getStatusFilterOptions = (onStatusFilterChange: (status: string) => void, onClose: () => void, t: (key: string) => string): FilterOption[] => [
    {key: EVENT_STATUSES.ALL, title: t('allStatuses'), onClick: () => {onStatusFilterChange(EVENT_STATUSES.ALL); onClose();}},
    {key: EVENT_STATUSES.ACTIVE, title: t('active'), onClick: () => {onStatusFilterChange(EVENT_STATUSES.ACTIVE); onClose();}},
    {key: EVENT_STATUSES.INACTIVE, title: t('inactive'), onClick: () => {onStatusFilterChange(EVENT_STATUSES.INACTIVE); onClose();}},
    {key: EVENT_STATUSES.EXPIRED, title: t('expired'), onClick: () => {onStatusFilterChange(EVENT_STATUSES.EXPIRED); onClose();}},
    {key: EVENT_STATUSES.DRAFT, title: t('draft'), onClick: () => {onStatusFilterChange(EVENT_STATUSES.DRAFT); onClose();}}
];

// Category filter options
export const getCategoryFilterOptions = (eventCategories: EventCategory[], onCategoryFilterChange: (category: string) => void, onClose: () => void, t: (key: string) => string): FilterOption[] => [
    {key: EVENT_STATUSES.ALL, title: t('allCategories'), onClick: () => {onCategoryFilterChange(EVENT_STATUSES.ALL); onClose();}},
    ...(eventCategories?.map((category: EventCategory) => ({
        key: category.catId,
        title: category.title,
        onClick: () => {onCategoryFilterChange(category.catId); onClose();}
    })) || [])
];
